/* eslint-disable max-lines */
import topLeftAndPivot from "./topleft_and_pivot.js"
import local2D from "./local_2D.js"
import Stroke from "./stroke.js"
import screenImageUtils from "./screen_image_utils.js"

const {rotateVector, pointsForRectangle, positionKeys} = topLeftAndPivot

const {referenceFrameFromGroupParams, referenceFrameFromScreenObject, localPointInReferenceFrame} = local2D

const {addImageDefaultPositionParams, normalizeTexture} = screenImageUtils



function init (Screen) {

    //public for other libs to use
    Screen.drawingMethods = proxifiedDrawingMethods.concat(Object.keys(api))
    Screen.paramsTypes    = paramsTypes
    Screen.addParamType   = addParamType

    Screen.cloneAndConvert = cloneAndConvert

    proxifyMethods(Screen)

    for (let name in api) {
        Screen.prototype[name] = api[name]
    }

    Screen.prototype.scaleUSyntax = function (text) {
        return scaleUSyntax(this, text)
    }

}



const api = {}


//****************************** STRAIGHTFORWARD PROXIFIED METHODS

/* NOTE FOR drawText :
*  Same params as in ScreenCanvas.
*  On 'font' params, size unit is in px, text won't scale.
*  To scale, use "u" instead of px : "5u georgia"
*/


let proxifiedDrawingMethods = [

    "drawLine",
    "drawArc",
    "drawPie",
    "drawCircle",
    "drawBezier",
    "drawBezierPath",
    "drawCurve",
    "drawFreeShape",
    "drawText",

    "drawPolygon", //FIXME remove soon
    "drawLines"    //FIXME remove soon
]


function proxifyMethods (Screen) {
    proxifiedDrawingMethods.forEach(function (drawingMethod) {
        Screen.prototype[drawingMethod] = proxyFunction(drawingMethod)
    })
}













//****************************** RECTANGLE

// geometry inputs baked into the rectangle's points ; every other param
// (style, texture, collision…) flows through to drawFreeShape untouched.
const rectangleShapeInputs = positionKeys.concat(["width", "height", "orientation", "pivot"])

api.drawRectangle = function (params) {

    //FIXME lineWidth is thicker when drawStraightRectangle is used
    /*if (!params.orientation && !params.relativeTo && !params.roundedCorners && !this.orientation) {
        drawStraightRectangle(this, params)
        return
    }*/
    const points = pointsForRectangle(params, this.yDir)

    params = paramsForFreeShape(params, {points, drop: rectangleShapeInputs})

    convertParams(this, params)

    if (params.roundedCorners) {
        applyRoundCorners(params)
        params.close = false
    }

    this.drawAdapter.drawFreeShape(params)

}


function drawStraightRectangle (screen, params) { //eslint-disable-line no-unused-vars
    params = cloneAndConvert(screen, params)
    screen.drawAdapter.drawRectangle(params)
}






//****************************** SPECIAL SHAPES


// inputs consumed to build the stroke envelope ; strokeColor is remapped to
// the fill color below, everything else flows through to drawFreeShape.
const strokeShapeInputs = ["stroke", "points", "width", "pointyEnds", "widthEnvelope", "strokeColor"]

api.drawStroke = function (params) {

    const stroke = strokeFromParams(params)

    // a stroke envelope is filled, not outlined : color falls back to strokeColor
    const color = getValue(params.color) ?? getValue(params.strokeColor)

    params = paramsForFreeShape(params, {
        points: stroke.envelope,
        drop:   strokeShapeInputs,
        color,
        close:  getValue(params.close) ?? false
    })

    convertParams(this, params)

    this.drawAdapter.drawFreeShape(params)
}


function strokeFromParams (params) {
    const stroke = getValue(params.stroke)
    if (stroke) {
        return stroke
    }
    const points     = getValue(params.points)
    const width      = getValue(params.width)
    const pointyEnds = getValue(params.pointyEnds)
    const {widthEnvelope} = params
    return new Stroke({points, width, widthEnvelope, pointyEnds})
}



const arrowDefaultBranchLengthRatio = 5
const arrowDefaultBranchAngle       = 30
const arrowAutoLineWithFactor       = 0.02


api.drawArrow = function (params) { //eslint-disable-line complexity
    let {
        branchLengthRatio = arrowDefaultBranchLengthRatio,
        branchAngle       = arrowDefaultBranchAngle,
        relativeTo,
        filled            = false,
        heads             = "end"
    } = params

    branchAngle       = getValue(branchAngle) + 180
    branchLengthRatio = getValue(branchLengthRatio)
    filled            = getValue(filled)
    heads             = getValue(heads)

    const origin = getValue(params.origin)
    if (!origin) {
        throw new Error('"origin" param is missing')
    }

    let vector = getValue(params.vector)
    if (!vector) {
        const end = getValue(params.end)
        if (!end) {
            throw new Error('"end" or "vector" param is missing')
        }
        vector = {
            x: end.x - origin.x,
            y: end.y - origin.y
        }
    }

    const vectorLength = length(vector.x, vector.y)

    let lineWidth
    let branchRatioToArrowLength
    if ("lineWidth" in params) {
        lineWidth = getValue(params.lineWidth)
        branchRatioToArrowLength = lineWidth * branchLengthRatio / vectorLength
    } else {
        lineWidth = arrowAutoLineWithFactor * vectorLength
        branchRatioToArrowLength = arrowAutoLineWithFactor * branchLengthRatio
    }

    const styleParams = {
        relativeTo,
        lineWidth,
        color:          params.color || params.strokeColor,
        opacity:        params.opacity,
        blendMode:      params.blendMode,
        filter:         params.filter,
        shadow:         params.shadow,
        blur:           params.blur,
        lineDash:       params.lineDash,
        lineDashOffset: params.lineDashOffset
    }

    const tip = {
        x: origin.x + vector.x,
        y: origin.y + vector.y
    }

    const forwardBranch = {
        x: vector.x * branchRatioToArrowLength,
        y: vector.y * branchRatioToArrowLength
    }

    this.drawLine(Object.assign({origin, vector}, styleParams))

    if (heads !== "start") {
        drawArrowHead(this, {tip, forwardBranch, branchAngle, filled, styleParams})
    }
    if (heads === "start" || heads === "both") {
        drawArrowHead(this, {
            tip:           origin,
            forwardBranch: {x: -forwardBranch.x, y: -forwardBranch.y},
            branchAngle,
            filled,
            styleParams
        })
    }
}


//a single stroke through [barb, tip, barb] avoids double-blending the tip when opacity < 1
function drawArrowHead (screen, {tip, forwardBranch, branchAngle, filled, styleParams}) {
    const branch1 = rotateVector(forwardBranch,  branchAngle)
    const branch2 = rotateVector(forwardBranch, -branchAngle)

    const points = [
        {x: tip.x + branch1.x, y: tip.y + branch1.y},
        tip,
        {x: tip.x + branch2.x, y: tip.y + branch2.y}
    ]

    if (filled) {
        screen.drawFreeShape(Object.assign({}, styleParams, {points, close: true}))
    } else {
        screen.drawCurve(Object.assign({}, styleParams, {points, close: false}))
    }
}





const degreesToRadians = Math.PI / 180

const angleDefaultColor       = "#06F"
const angleRadiusToRayRatio   = 0.25
const angleFontToRadiusRatio  = 0.35
const angleLabelInsideRatio   = 0.62
const angleLabelGapRatio      = 0.5    //between the arc and the label box, in font sizes
const angleTextWidthPerChar   = 0.6    //rough average glyph width, in font sizes
const rightAngleDegrees        = 90
const rightAngleEpsilon        = 1e-6
const rightAngleSideRatio      = Math.SQRT1_2  //bare mark (squareOnly) : the corner lands where the arc would be
const rightAngleOutlineRatio   = 0.4           //small decoration nested at the vertex, inside the wedge

const missingAngleRaysMessage = 'angle rays are missing : give "from"/"to" points, "startAngle"/"endAngle" degrees, or one of them with a sweep "angle"'


//draws an angle mark (arc + wedge + measure label) at a vertex, for maths teaching.
//Rays given as points (from/to) or degrees (startAngle/endAngle), or a sweep (angle).
//Always counterclockwise ; a negative sweep goes clockwise. 90° gets a square mark.
//Returns the measured angle in degrees.
api.drawAngle = function (params) {
    const center = getValue(params.center)
    if (!center) {
        throw new Error('"center" param is missing')
    }

    const {startAngle, sweep, radius} = normalizedAngleParams(center, params)

    const color     = getValue(params.color)
    let strokeColor = getValue(params.strokeColor)
    if (!color && !strokeColor) {
        strokeColor = angleDefaultColor
    }

    const commonParams = {
        relativeTo:     params.relativeTo,
        lineWidth:      params.lineWidth,
        opacity:        params.opacity,
        blendMode:      params.blendMode,
        filter:         params.filter,
        shadow:         params.shadow,
        blur:           params.blur,
        lineDash:       params.lineDash,
        lineDashOffset: params.lineDashOffset
    }

    const isRightAngle = Math.abs(sweep - rightAngleDegrees) < rightAngleEpsilon
    const markParams   = {center, radius, startAngle, sweep, isRightAngle, color, strokeColor, commonParams}

    drawAngleMark(this, markParams, getValue(params.squareOnly))
    drawAngleLabel(this, markParams, params)

    return sweep
}


function drawAngleMark (screen, markParams, squareOnly) {
    if (markParams.isRightAngle && squareOnly) {
        drawRightAngleSquare(screen, markParams)
        return
    }

    drawAngleArc(screen, markParams)

    if (markParams.isRightAngle) {
        drawRightAngleOutline(screen, markParams)
    }
}


function normalizedAngleParams (center, params) { //eslint-disable-line complexity
    const from  = getValue(params.from)
    const to    = getValue(params.to)
    const angle = getValue(params.angle)

    let start = from ? directionToward(center, from) : getValue(params.startAngle)
    let end   = to   ? directionToward(center, to)   : getValue(params.endAngle)

    let sweep
    if (typeof end === "undefined") {
        if (typeof start === "undefined" || typeof angle === "undefined") {
            throw new Error(missingAngleRaysMessage)
        }
        sweep = angle
    } else if (typeof start === "undefined") {
        if (typeof angle === "undefined") {
            throw new Error(missingAngleRaysMessage)
        }
        start = end - angle
        sweep = angle
    } else {
        sweep = modulo360(end - start)
    }

    if (sweep < 0) {
        start += sweep
        sweep  = -sweep
    }

    return {
        startAngle: start,
        sweep,
        radius: angleMarkRadius(center, {from, to}, params)
    }
}


function angleMarkRadius (center, {from, to}, params) {
    const radius = getValue(params.radius)
    if (typeof radius !== "undefined") {
        return radius
    }

    const rayLengths = [from, to]
        .filter(Boolean)
        .map(point => length(point.x - center.x, point.y - center.y))

    if (rayLengths.length === 0) {
        throw new Error('"radius" param is missing (only needed when no "from"/"to" point is given)')
    }

    return angleRadiusToRayRatio * Math.min(...rayLengths)
}


function drawAngleArc (screen, {center, radius, startAngle, sweep, color, strokeColor, commonParams}) {
    const angleParams = {center, radius, startAngle, endAngle: startAngle + sweep}

    if (color) {
        screen.drawPie(Object.assign({color}, angleParams, commonParams))
    }
    if (strokeColor) {
        screen.drawArc(Object.assign({strokeColor, close: false}, angleParams, commonParams))
    }
}


function rightAngleSquarePoints (center, startAngle, side) {
    const p1 = pointAtAngle(center, startAngle, side)
    const p2 = pointAtAngle(center, startAngle + rightAngleDegrees, side)
    return {
        p1,
        p2,
        corner: {
            x: p1.x + p2.x - center.x,
            y: p1.y + p2.y - center.y
        }
    }
}


//the classic bare mark (squareOnly) : a filled square, its two outer edges stroked
function drawRightAngleSquare (screen, {center, radius, startAngle, color, strokeColor, commonParams}) {
    const {p1, corner, p2} = rightAngleSquarePoints(center, startAngle, radius * rightAngleSideRatio)

    if (color) {
        screen.drawFreeShape(Object.assign({points: [center, p1, corner, p2], close: true, color}, commonParams))
    }
    if (strokeColor) {
        screen.drawCurve(Object.assign({points: [p1, corner, p2], close: false, strokeColor}, commonParams))
    }
}


//on top of the usual wedge + arc : a small square decoration nested at the vertex
function drawRightAngleOutline (screen, {center, radius, startAngle, color, strokeColor, commonParams}) {
    const {p1, corner, p2} = rightAngleSquarePoints(center, startAngle, radius * rightAngleOutlineRatio)

    screen.drawCurve(Object.assign({
        points:      [p1, corner, p2],
        close:       false,
        strokeColor: strokeColor || color
    }, commonParams))
}


function angleLabelText (params, sweep) {
    const text = getValue(params.text)
    if (text === false) {
        return false
    }
    if (typeof text === "undefined" || text === true) {
        return angleValueText(sweep)
    }
    return String(text)
}


function drawAngleLabel (screen, {center, radius, startAngle, sweep, isRightAngle, color, strokeColor}, params) {
    const text = angleLabelText(params, sweep)
    if (text === false) {
        return
    }

    const font     = getValue(params.font)
    const fontSize = angleFontWorldSize(screen, font, radius)

    const bisector = startAngle + sweep / 2

    screen.drawText({
        text,
        center:     pointAtAngle(center, bisector, angleLabelDistance({radius, sweep, isRightAngle, fontSize, text, bisector})),
        font:       font || `${parseFloat(fontSize.toPrecision(4))}u sans-serif`,
        color:      getValue(params.textColor) || strokeColor || color,
        relativeTo: params.relativeTo,
        opacity:    params.opacity
    })
}


//inside the wedge when the text fits across it, otherwise pushed beyond the arc.
//the square mark leaves no room : its label always goes outside
function angleLabelDistance ({radius, sweep, isRightAngle, fontSize, text, bisector}) {
    const insideDistance = radius * angleLabelInsideRatio
    const textWidth      = angleTextWidthPerChar * fontSize * text.length
    const availableWidth = 2 * insideDistance * Math.sin(Math.min(sweep, 180) * degreesToRadians / 2)

    if (!isRightAngle && textWidth <= availableWidth) {
        return insideDistance
    }

    //the horizontal label box must clear the arc : add its half-extent along the bisector
    const bisectorRad = bisector * degreesToRadians
    const textReach   = (textWidth / 2) * Math.abs(Math.cos(bisectorRad)) +
                        (fontSize  / 2) * Math.abs(Math.sin(bisectorRad))

    return radius + angleLabelGapRatio * fontSize + textReach
}


const angleFontUSizeRegexp  = /([\d.]+)u\b/
const angleFontPxSizeRegexp = /([\d.]+)px\b/

//world-units estimate of the label font size, used for the default font and placement
function angleFontWorldSize (screen, font, radius) {
    if (font) {
        const uMatch = font.match(angleFontUSizeRegexp)
        if (uMatch) {
            return parseFloat(uMatch[1])
        }
        const pxMatch = font.match(angleFontPxSizeRegexp)
        if (pxMatch) {
            return parseFloat(pxMatch[1]) / screen.scale
        }
    }
    return radius * angleFontToRadiusRatio
}


//one decimal reads well for classroom measures ; tiny angles keep two significant digits
function angleValueText (degrees) {
    let value = Math.round(degrees * 10) / 10
    if (value === 0 && degrees !== 0) {
        value = parseFloat(degrees.toPrecision(2))
    }
    return `${value}°`
}


function directionToward (center, point) {
    return Math.atan2(point.y - center.y, point.x - center.x) / degreesToRadians
}


function pointAtAngle (center, angleDeg, distance) {
    const rad = angleDeg * degreesToRadians
    return {
        x: center.x + Math.cos(rad) * distance,
        y: center.y + Math.sin(rad) * distance
    }
}


function modulo360 (angle) {
    return ((angle % 360) + 360) % 360
}




const defaultAxesLineWidthFactor = 0.01

api.drawAxes = function (params) {

    const orientation = getValue(params.orientation)
    const center      = getValue(params.center) || {x: 0, y: 0}

    let {xMin, yMin, width, height} = boundingBoxForGrid(this, {center, orientation})

    const color   = getValue(params.color) || "#06F"
    const opacity = getValue(params.opacity)

    drawCrossAxes(this, {
        width,
        height,
        localXMin: xMin,
        localYMin: yMin,
        center,
        orientation,
        lineWidth: getValue(params.lineWidth),
        color,
        opacity
    })

}



api.drawGrid = function (params = {}) { //eslint-disable-line complexity

    const orientation = getValue(params.orientation)
    const center      = getValue(params.center) || {x: 0, y: 0}

    let cachedBox
    let screen = this
    function box () {
        if (!cachedBox) {
            cachedBox = boundingBoxForGrid(screen, {center, orientation})
        }
        return cachedBox
    }

    const step = getValue(params.step) || autoGridStep(box())

    const stepSmallerThanAPixel = (step * this.scale < 1)
    if (stepSmallerThanAPixel) {
        return
    }

    const color        = getValue(params.color)
    const opacityLimit = getValue(params.opacityLimit)
    const lineWidth    = getValue(params.lineWidth)
    let {axes = true} = params
    axes = getValue(axes)


    let localXMin = getValue(params.localXMin)
    let localXMax = getValue(params.localXMax)

    let width
    if (typeof localXMin !== "undefined" && typeof localXMax !== "undefined") {
        width = localXMax - localXMin
    } else {
        width = getValue(params.width)
        if (typeof width === "undefined") {
            width = box().width
            if (typeof localXMin === "undefined") {
                localXMin = box().xMin
            }
        } else if (typeof localXMin === "undefined") {
            localXMin = -width / 2
        }
    }


    let localYMin = getValue(params.localYMin)
    let localYMax = getValue(params.localYMax)

    let height
    if (typeof localYMin !== "undefined" && typeof localYMax !== "undefined") {
        height = localYMax - localYMin
    } else {
        height = getValue(params.height)
        if (typeof height === "undefined") {
            height = box().height
            if (typeof localYMin === "undefined") {
                localYMin = box().yMin
            }
        } else if (typeof localYMin === "undefined") {
            localYMin = -height / 2
        }
    }

    drawCrossGuides(this, {
        width,
        height,
        localXMin,
        localYMin,
        step,
        center,
        color,
        opacityLimit,
        axes,
        orientation,
        lineWidth
    })

}





const pointsGridStepMinPixelSize  = 8
const pointsGridBigRadiusFactor   = 0.1
const pointsGridSmallRadiusFactor = 0.06
const pointsGridDefaultMajorEvery = 5

api.drawPointsGrid = function (params) { //eslint-disable-line complexity
    const step  = getValue(params.step)  || 1
    const color = getValue(params.color) || "#EEE"

    const tooSmallToView = (step * this.scale < pointsGridStepMinPixelSize)
    if (tooSmallToView) {
        return
    }

    const dotParams = {
        step,
        color,
        opacity:     getValue(params.opacity),
        bigRadius:   getValue(params.bigRadius)   || step * pointsGridBigRadiusFactor,
        smallRadius: getValue(params.smallRadius) || step * pointsGridSmallRadiusFactor,
        majorEvery:  getValue(params.majorEvery)  || pointsGridDefaultMajorEvery
    }

    const orientation = getValue(params.orientation)
    const center      = getValue(params.center)

    if (orientation || center) {
        drawOrientedPointsGrid(this, Object.assign({center: center || {x: 0, y: 0}, orientation}, dotParams))
        return
    }

    const boundingBox = getValue(params.boundingBox) || this.boundingBoxForWholeView()
    const {xMin, yMin, width, height} = boundingBox

    const xStart = Math.floor(xMin / step)
    const yStart = Math.floor(yMin / step)
    const xStop  = xStart + (width  / step) + 1
    const yStop  = yStart + (height / step) + 1

    for (let y = yStart; y < yStop; y++) {
        for (let x = xStart; x < xStop; x++) {
            this.drawCircle({
                center:  {x: x * step, y: y * step},
                radius:  pointsGridDotRadius(x, y, dotParams),
                color,
                opacity: dotParams.opacity
            })
        }
    }
}


//big dots mark the major intersections (every "majorEvery" steps), anchored on the grid origin/center
function pointsGridDotRadius (i, j, {bigRadius, smallRadius, majorEvery}) {
    const isMajor = (i % majorEvery === 0) && (j % majorEvery === 0)
    return isMajor ? bigRadius : smallRadius
}


function drawOrientedPointsGrid (screen, {center, orientation = 0, step, color, opacity, bigRadius, smallRadius, majorEvery}) {
    const box = boundingBoxForGrid(screen, {center, orientation})

    const unitHorizontal = rotateVector({x: 1, y: 0}, orientation)
    const unitVertical   = rotateVector({x: 0, y: 1}, orientation)

    const iStart = Math.floor(box.xMin / step)
    const jStart = Math.floor(box.yMin / step)
    const iStop  = iStart + (box.width  / step) + 1
    const jStop  = jStart + (box.height / step) + 1

    for (let j = jStart; j < jStop; j++) {
        for (let i = iStart; i < iStop; i++) {
            const lx = i * step
            const ly = j * step
            screen.drawCircle({
                center: {
                    x: center.x + unitHorizontal.x * lx + unitVertical.x * ly,
                    y: center.y + unitHorizontal.y * lx + unitVertical.y * ly
                },
                radius: pointsGridDotRadius(i, j, {bigRadius, smallRadius, majorEvery}),
                color,
                opacity
            })
        }
    }
}








//****************************** DEBUG DRAWING


const pointRadius = 0.05

api.drawDebugReference = function (params = {}) {

    const o = getValue(params.o) || {x: 0, y: 0}
    const i = getValue(params.i) || {x: 1, y: 0}
    const j = getValue(params.j) || {x: 0, y: 1}

    const color = getValue(params.color) || "#B20000"
    const scale = getValue(params.scale) || 1

    this.drawCircle({
        center:    o,
        radius:    pointRadius * scale,
        color
    })

    this.drawArrow({
        origin: o,
        vector: {
            x: i.x * scale,
            y: i.y * scale
        },
        color
    })

    this.drawArrow({
        origin: o,
        vector: {
            x: j.x * scale,
            y: j.y * scale
        },
        color
    })
}


api.drawDebugGrid = function ({color, step} = {}) {
    this.drawGrid({
        color,
        step,
        opacityLimit: {
            max: 0.5,
            min: 0.1
        }
    })
}


api.drawVectorFrame = function ({scale, color = "#F00"} = {}) {
    this.drawDebugReference({color, scale})
}


api.drawCameraBox = function (cameraParams, otherParams = {}) {
    if (typeof cameraParams === "function") {
        cameraParams = cameraParams()
    }
    const {center, orientation, scale} = cameraParams
    const {strokeColor = "#FA0", color = false} = otherParams

    let width  = this.width  / scale
    let height = this.height / scale
    this.drawRectangle({
        center,
        width,
        height,
        orientation,
        strokeColor,
        color,
        lineWidth: 10 / scale
    })
}






//****************************** IMAGE

api.drawImage = function (paramsOrScreenImage) {

    let params = paramsOrScreenImage
    if (!paramsOrScreenImage.image) {
        params = {image: paramsOrScreenImage}
    }

    let {queueForAfterLoad} = params.image || {}

    if (queueForAfterLoad) {
        queueForAfterLoad.push(() => drawImage(this, params))
    } else {
        drawImage(this, params)
    }

}



function drawImage (screen, params) {
    params = clone(params)

    const {image} = params
    if (image) {
        addImageDefaultPositionParams(params, image)
    }

    convertParams(screen, params)


    // FIXME should be in screen_canvas
    /*let pixelBox
    if (params.modifier) {
        pixelBox           = this.pixBoundingBox(params)
        ghostCanvas.width  = pixelBox.width
        ghostCanvas.height = pixelBox.height
        originPix.x       -= pixelBox.xMin
        originPix.y       -= pixelBox.yMax
    }*/


    //FIXME move in screen_canvas
    // params.modifierPixelBox = pixelBox

    screen.drawAdapter.drawImage(params)
}



//****************************** CUSTOM HOOK


//FIXME
api.drawCustom = function (drawingFunction) {
    const {drawAdapter, adapterScale, adapterScaleY, center, centerPix} = this
    const {cx, canvasStyle} = drawAdapter

    const x = centerPix.x - center.x * adapterScale
    const y = centerPix.y - center.y * adapterScaleY

    function useScreenCoords () {
        cx.setTransform(adapterScale, 0, 0, adapterScaleY, x, y)
    }

    drawingFunction(cx, {x, y, scale: adapterScale, useScreenCoords})

    cx.setTransform(1, 0, 0, 1, 0, 0)
    canvasStyle.resyncWithCurrentStyle()
    canvasStyle.resetSpecialStyles()
}



//****************************** GROUP


let currentGroupDepth = 0

api.drawGroup = function (params) {  //eslint-disable-line complexity

    let {maxDepth = this.groupMaxDepth} = params

    if (typeof maxDepth === "function") {
        maxDepth = maxDepth()
    }

    if (currentGroupDepth >= Math.min(maxDepth, this.groupMaxDepth)) {
        return
    }

    const {startIndex = 0, view, group = view} = params
    let {opacity = 1} = params.viewPosition || params
    opacity = getValue(opacity)

    let parentOpacity
    if (opacity !== 1) {
        parentOpacity = this.drawAdapter.getOpacity()
        this.drawAdapter.setOpacity(parentOpacity * opacity)
    }


    let {elements, methods, rasterization} = getValue(group)

    let referenceFrame = params.viewPosition ? referenceFrameFromScreenObject(params) : referenceFrameFromGroupParams(params)


    if (rasterization) {
        if (referenceFrame) {
            rasterization = composeParamsWithReferenceFrame(rasterization, referenceFrame)
        }
        this.drawImage(rasterization)
        return
    }

    currentGroupDepth += 1

    let screen = this

    let drawMethod

    if (referenceFrame) {
        drawMethod = function (methodName, methodParams) {
            screen[methodName](composeParamsWithReferenceFrame(methodParams, referenceFrame))
        }
    } else {
        drawMethod = function (methodName, methodParams) {
            screen[methodName](methodParams)
        }
    }

    const {cx} = this.drawAdapter
    let oldComposite = cx.globalCompositeOperation


    let index
    try {
        for (index = startIndex; index < elements.length; index++) {
            drawMethod(methods[index], elements[index])
        }
    } catch (error) {
        methods.length  = index
        elements.length = index

        error.toxiTrace = error.toxiTrace || []
        error.toxiTrace.unshift("Toxilibs - screen_group.js - render")
        const {errorHandler} = this
        if (errorHandler) {
            errorHandler(error)
        } else {
            currentGroupDepth -= 1
            throw error
        }
    }



    if (opacity !== 1) {
        this.drawAdapter.setOpacity(parentOpacity)
    }

    if (cx.globalCompositeOperation !== oldComposite) {
        cx.globalCompositeOperation = oldComposite
    }

    currentGroupDepth -= 1
}



function composeParamsWithReferenceFrame (params, referenceFrame) {
    let givenReferenceFrame = params.relativeTo
    let newReferenceFrame
    if (givenReferenceFrame) {
        newReferenceFrame = referenceFrame.globalBase(givenReferenceFrame)
    } else {
        newReferenceFrame = referenceFrame
    }
    return Object.assign({}, params, {relativeTo: newReferenceFrame})
}






//****************************** GRID TOOLS




function humanStepChoice (x, digit) {
    return Math.pow(10, Math.floor(Math.log10(x / digit))) * digit
}

/* eslint-disable no-magic-numbers */

function autoGridStep ({width, height}) {
    let size = Math.min(width, height) / 6
    return Math.max(humanStepChoice(size, 1), humanStepChoice(size, 2), humanStepChoice(size, 5))
}

/* eslint-enable no-magic-numbers */


function boundingBoxForGrid (screen, {center, orientation = 0}) {
    let topLeftScreenCorner = screen.pixelCoordsToCoords({x: 0, y: 0})
    let topLeft = localPointInReferenceFrame({
        x: center.x,
        y: center.y,
        orientation
    }, topLeftScreenCorner.x, topLeftScreenCorner.y)

    return screen.boundingBox({
        topLeft,
        width:  screen.width  / screen.scale,
        height: screen.height / screen.scale,
        orientation: -orientation - screen.orientation
    })
}


function drawCrossGuides (screen, {
    height, width,
    localXMin, localYMin,
    step,
    center, orientation = 0,
    color, opacityLimit, axes, lineWidth
}) {

    let unitHorizontal = rotateVector({x: 1, y: 0}, orientation)
    let unitVertical   = rotateVector({x: 0, y: 1}, orientation)

    drawGuideLines(screen, {
        axes,
        startIndex: Math.ceil(localXMin / step),
        endIndex:   Math.floor((localXMin + width) / step),
        center: {
            x: center.x + unitVertical.x * localYMin,
            y: center.y + unitVertical.y * localYMin
        },
        lineVector: {
            x: unitVertical.x * height,
            y: unitVertical.y * height
        },
        stepVector: {
            x: unitHorizontal.x * step,
            y: unitHorizontal.y * step
        },
        color,
        opacityLimit,
        lineWidth
    })

    drawGuideLines(screen, {
        axes,
        startIndex: Math.ceil(localYMin / step),
        endIndex:   Math.floor((localYMin + height) / step),
        center: {
            x: center.x + unitHorizontal.x * localXMin,
            y: center.y + unitHorizontal.y * localXMin
        },
        lineVector: {
            x: unitHorizontal.x * width,
            y: unitHorizontal.y * width
        },
        stepVector: {
            x: unitVertical.x * step,
            y: unitVertical.y * step
        },
        color,
        opacityLimit,
        lineWidth
    })
}



function drawCrossAxes (screen, {
    height, width,
    localXMin, localYMin,
    center, orientation = 0,
    lineWidth = Math.min(width, height) * defaultAxesLineWidthFactor,
    opacity = 1,
    color = "#888"
}) {

    let unitHorizontal = rotateVector({x: 1, y: 0}, orientation)
    let unitVertical   = rotateVector({x: 0, y: 1}, orientation)

    screen.drawLine({
        origin: {
            x: center.x + unitVertical.x * localYMin,
            y: center.y + unitVertical.y * localYMin
        },
        vector: {
            x: unitVertical.x * height,
            y: unitVertical.y * height
        },
        color,
        opacity,
        lineWidth
    })

    screen.drawLine({
        origin: {
            x: center.x + unitHorizontal.x * localXMin,
            y: center.y + unitHorizontal.y * localXMin
        },
        vector: {
            x: unitHorizontal.x * width,
            y: unitHorizontal.y * width
        },
        color,
        opacity,
        lineWidth
    })

}





const guideLineWidth = 0.02

function doubleLength (value) {
    if (typeof value === "string") {
        return `${parseFloat(value, 10) * 2}px`
    }
    return value * 2
}

function drawGuideLines (screen, { //eslint-disable-line complexity
    axes = true,
    startIndex,
    endIndex,
    center,
    lineVector,
    stepVector,
    color = "#888",
    opacityLimit = {max: 1, min: 0.4},
    lineWidth
}) {


    let step = length(stepVector.x, stepVector.y)
    if (typeof lineWidth === "undefined") {
        lineWidth = step * guideLineWidth
    }
    let axesLineWidth = doubleLength(lineWidth)

    if (typeof opacityLimit === "number") {
        opacityLimit = {
            max: opacityLimit,
            min: opacityLimit
        }
    }

    for (let i = startIndex; i <= endIndex; i += 1) {

        let lineOrigin = {
            x: center.x + stepVector.x * i,
            y: center.y + stepVector.y * i
        }

        let opacity          = opacityLimit.min
        let currentLineWidth = lineWidth

        if (axes && i === 0) {
            opacity   = opacityLimit.max
            currentLineWidth = axesLineWidth
        }

        screen.drawLine({
            origin: lineOrigin,
            vector: lineVector,
            color,
            opacity,
            lineWidth: currentLineWidth
        })
    }
}








//****************************** RECTANGLE




const roundCornerControlRatio  = 0.5
const roundCornerControlRatio2 = -0.25

function applyRoundCorners (params) {
    const {points, roundedCorners} = params
    const newPoints = []

    points.forEach(function (point, i) {
        let otherPoint = points[(i + 1) % points.length]
        let pA = pointToward(point,      otherPoint, roundedCorners)
        let pB = pointToward(otherPoint, point,      roundedCorners)
        pA.controlPrevious = vectorBetween(pA, point,      roundCornerControlRatio)
        pA.controlNext     = vectorBetween(pA, point,      roundCornerControlRatio2)
        pB.controlPrevious = vectorBetween(pB, otherPoint, roundCornerControlRatio2)
        pB.controlNext     = vectorBetween(pB, otherPoint, roundCornerControlRatio)
        newPoints.push(pA, pB)
    })

    newPoints.push(newPoints[0])
    params.points = newPoints
}



function pointToward (start, target, distance) {
    let d = length(target.x - start.x, target.y - start.y)

    let ratio = (d === 0 ? 0 : distance / d)

    return pointBetween(start, target, ratio)
}


function pointBetween (pointA, pointB, position) {

    return {
        x: pointA.x * (1 - position) + (pointB.x * position),
        y: pointA.y * (1 - position) + (pointB.y * position)
    }

}


function vectorBetween (pointA, pointB, position) {

    return {
        x: (pointB.x - pointA.x) * position,
        y: (pointB.y - pointA.y) * position
    }

}




//****************************** CONVERSIONS



function proxyFunction (name) {
    return function (params) {
        params = cloneAndConvert(this, params)
        this.drawAdapter[name](params)
    }
}


function convertCoords (screen, coords, referenceFrame) {
    return screen.coordsToAdapterCoords(coords, referenceFrame)
}

function convertVector (screen, coords, referenceFrame) {
    return screen.vectorCoordsToAdapterCoords(coords, referenceFrame)
}


function convertLength (screen, value, referenceFrame) {
    if (referenceFrame) {
        if ("globalLength" in referenceFrame) {
            return screen.adapterScale * referenceFrame.globalLength(value)
        } else {
            let {scale = 1} = referenceFrame
            return screen.adapterScale * value * scale
        }
    } else {
        return screen.adapterScale * value
    }
}


function convertSmartLength (screen, value, referenceFrame) {
    if (typeof value === "string" && value.endsWith("px")) {
        return parseFloat(value, 10)
    } else {
        return convertLength(screen, value, referenceFrame)
    }
}


function convertSmartLengthArray (screen, value, referenceFrame) {
    if (typeof value === "string") {
        return value
    }
    return value.map(v => convertSmartLength(screen, v, referenceFrame))
}



function convertPoints (screen, points, referenceFrame) {
    return points.map(function ({x, y, controlPrevious, controlNext}) {
        let pixPoint = screen.coordsToAdapterCoords({x, y}, referenceFrame)
        if (controlPrevious) {
            pixPoint.controlPrevious = screen.vectorCoordsToAdapterCoords(controlPrevious, referenceFrame)
        }
        if (controlNext) {
            pixPoint.controlNext = screen.vectorCoordsToAdapterCoords(controlNext, referenceFrame)
        }
        return pixPoint
    })
}


function convertBeziers (screen, beziers, referenceFrame) {
    return beziers.map(function ({origin, end, controlOrigin, controlEnd}) {
        return {
            origin:        screen.coordsToAdapterCoords(origin, referenceFrame),
            end:           screen.coordsToAdapterCoords(end, referenceFrame),
            controlOrigin: screen.coordsToAdapterCoords(controlOrigin, referenceFrame),
            controlEnd:    screen.coordsToAdapterCoords(controlEnd, referenceFrame)
        }
    })
}


function convertAngle (screen, angle, referenceFrame) {
    if (referenceFrame) {
        if ("globalLength" in referenceFrame) {
            return screen.orientation + referenceFrame.globalOrientation(angle)
        } else {
            return screen.orientation + (referenceFrame.orientation || 0) + angle
        }
    } else {
        return screen.orientation + angle
    }
}


function convertCoordsAndRadius (screen, coordsAndRadius, referenceFrame) {
    let result = screen.coordsToAdapterCoords(coordsAndRadius, referenceFrame)
    result.radius = convertLength(screen, coordsAndRadius.radius, referenceFrame)
    return result
}


function convertGradientParams (screen, params, referenceFrame) {
    params = clone(params)
    params.relativeTo = referenceFrame
    convertParams(screen, params)
    return params
}


function convertTextureParams (screen, value, referenceFrame) {
    const tex = normalizeTexture(value)
    if (!tex) {
        return tex
    }

    const result = Object.assign({}, tex)

    if (tex.origin) {
        result.origin = screen.coordsToAdapterCoords(tex.origin, referenceFrame)
    }
    convertTextureSize(screen, tex, result, referenceFrame)
    if ("orientation" in tex) {
        result.orientation = convertAngle(screen, tex.orientation, referenceFrame)
    }

    return result
}


function convertTextureSize (screen, tex, result, referenceFrame) {
    if ("width" in tex) {
        result.width = convertLength(screen, tex.width, referenceFrame)
    }
    if ("height" in tex) {
        result.height = convertLength(screen, tex.height, referenceFrame)
    }
    if ("scale" in tex) {
        result.scale = convertTextureScale(screen, tex.scale, referenceFrame)
    } else if (!("width" in tex) && !("height" in tex)) {
        result.scale = convertLength(screen, 1, referenceFrame)
    }
}


function convertTextureScale (screen, scale, referenceFrame) {
    if (typeof scale === "object") {
        return {
            x: convertLength(screen, scale.x, referenceFrame),
            y: convertLength(screen, scale.y, referenceFrame)
        }
    }
    return convertLength(screen, scale, referenceFrame)
}


function convertShadowParams (screen, params, referenceFrame) {
    const {blur, opacity, color, xOffset: x = 0, yOffset: y = 0} = params

    const {x: xOffset, y: yOffset} = screen.vectorCoordsToAdapterCoords({x, y}, referenceFrame)


    return {
        xOffset,
        yOffset,
        blur: blur && convertLength(screen, blur, referenceFrame),
        opacity,
        color
    }
}


const uSyntaxRegexp = /((\d+\.\d+)|(\d+\.?)|(\.\d+))u\b/g

function scaleUSyntax (screen, text, referenceFrame) {
    return text.replace(uSyntaxRegexp, function (match, numericPart) {
        return `${Math.round(convertLength(screen, parseFloat(numericPart, 10), referenceFrame))}px`
    })
}



let paramsTypes = {
    relativeTo:     "referenceFrame",

    origin:         "coords",
    end:            "coords",
    from:           "coords",
    to:             "coords",
    controlOrigin:  "coords",
    controlEnd:     "coords",
    topLeft:        "coords",
    topRight:       "coords",
    bottomLeft:     "coords",
    bottomRight:    "coords",
    center:         "coords",
    topCenter:      "coords",
    bottomCenter:   "coords",
    centerRight:    "coords",
    centerLeft:     "coords",
    top:            "coords",
    bottom:         "coords",
    right:          "coords",
    left:           "coords",

    innerCircle:    "coordsAndRadius",
    outerCircle:    "coordsAndRadius",

    radius:         "length",
    innerRadius:    "length",
    bigRadius:      "length",
    smallRadius:    "length",
    lineWidth:      "smartLength",

    vector:         "vector",
    pivot:          "vector",

    orientation:    "angle",
    angle:          "angle",
    startAngle:     "angle",
    endAngle:       "angle",
    branchAngle:    "angle",

    points:         "points",

    beziers:        "beziers",

    linearGradient: "linearGradient",
    radialGradient: "radialGradient",

    texture:        "texture",
    strokeTexture:  "texture",

    font:           "textWithUSyntax",
    lineHeight:     "factor",
    align:          "text",

    //FIXME not used here but in ScreenGroup
    step:           "length",
    offset:         "length",
    width:          "length",
    height:         "length",
    roundedCorners: "length",
    x:              "length",
    y:              "length",
    localXMin:      "length",
    localXMax:      "length",
    localYMin:      "length",
    localYMax:      "length",

    stroke:         "stroke",
    widthEnvelope:  "function",

    smoothing:      "boolean",
    pixelated:      "boolean",
    close:          "boolean",
    squareOnly:     "boolean",
    pointyEnds:     "boolean",
    axes:           "boolean",
    filled:         "boolean",

    disableCollision: "boolean",

    useFontMetrics: "boolean",

    branchLengthRatio: "factor",
    smooth:         "factor",
    curviness:      "factor",
    opacity:        "factor",
    opacityLimit:   "factor",
    scale:          "factor",

    blendMode:      "text",
    filter:         "textWithUSyntax",
    shadow:         "shadow",
    blur:           "smartLength",
    flip:           "text",
    lineDash:       "smartLengthArray",
    lineDashOffset: "smartLength",

    color:          "color",
    fillColor:      "color",
    strokeColor:    "color",
    textColor:      "color",
    image:          "image",
    imageData:      "imageData",
    group:          "group",
    text:           "text",
    heads:          "text",
    url:            "text",
    name:           "text",
    format:         "text",
    fileName:       "text",
    type:           "text",
    cropSource:     "cropParams",

    quality:        "number",
    majorEvery:     "number",

    layer:          "number",
    maxDepth:       "number"
}



let typeToConversionFunction = {
    coords:          convertCoords,
    coordsAndRadius: convertCoordsAndRadius,
    length:          convertLength,
    vector:          convertVector,
    angle:           convertAngle,
    orientation:     convertAngle,
    points:          convertPoints,
    beziers:         convertBeziers,
    linearGradient:  convertGradientParams, //FIXME make it more specific
    radialGradient:  convertGradientParams, //FIXME make it more specific
    texture:         convertTextureParams,
    shadow:           convertShadowParams,
    textWithUSyntax:  scaleUSyntax,
    smartLength:      convertSmartLength,
    smartLengthArray: convertSmartLengthArray
}


let conversionFunctions = {}
for (let paramName in paramsTypes) {
    conversionFunctions[paramName] = typeToConversionFunction[paramsTypes[paramName]]
}


function addParamType (paramName, type) {
    paramsTypes[paramName] = type
    conversionFunctions[paramName] = typeToConversionFunction[type]
}


function cloneAndConvert (screen, params) {
    let newParams = {}
    convertParams(screen, params, newParams)
    return newParams
}



// targetParams is optional, and is sourceParams by default.
function convertParams (screen, sourceParams, targetParams) { //eslint-disable-line complexity
    let targetIsSource = false

    if (!targetParams) {
        targetParams   = sourceParams
        targetIsSource = true
    }

    const referenceFrame = getValue(sourceParams.relativeTo)


    for (let key in sourceParams) {
        if (!(key in paramsTypes)) {
            continue
        }

        let value = sourceParams[key]
        if (typeof value === "undefined") {
            continue
        }

        let conversionFunction = conversionFunctions[key]

        if (typeof value === "function") {
            if (key === "relativeTo") {
                value = referenceFrame
            } else {
                value = value()
            }
            if (targetIsSource && !conversionFunction) {
                targetParams[key] = value
            }
        }

        if (conversionFunction) {
            targetParams[key] = conversionFunction(screen, value, referenceFrame)
        } else if (!targetIsSource) {
            targetParams[key] = value
        }
    }

    if (!("orientation" in sourceParams) && (screen.orientation || referenceFrame)) {
        targetParams.orientation = convertAngle(screen, 0, referenceFrame)
    }

}


function length (x, y) {
    return Math.sqrt(x * x + y * y)
}



function getValue (value) {
    if (typeof value === "function") {
        return value()
    } else {
        return value
    }
}


function clone (data) {
    let newData = {}
    for (let key in data) {
        newData[key] = data[key]
    }
    return newData
}


// builds the params handed to drawFreeShape : keeps every style/forwarded param,
// drops only the geometry inputs already baked into `points`, and overrides
// points / color / close. New param types need no edit here.
function paramsForFreeShape (params, {points, drop, color, close}) {
    const newParams = clone(params)
    drop.forEach(key => delete newParams[key])
    newParams.points = points
    if (typeof color !== "undefined") {
        newParams.color = color
    }
    if (typeof close !== "undefined") {
        newParams.close = close
    }
    return newParams
}



export default init

